import { ChangeDetectionStrategy, Component } from '@angular/core';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { CopyToClipboardDirective } from '@examdojo/core/copy-to-clipboard';
import { isNotNullish } from '@examdojo/util/nullish';
import posthog from 'posthog-js';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { DebuggingContextService } from '../debugging-context.service';

@Component({
  selector: 'dojo-issue-report',
  imports: [CopyToClipboardDirective, ButtonComponent],
  template: `
    <div>
      @if (posthogSessionReplayUrl) {
        <p class="truncate">
          PostHog session url:
          <a class="primary-link" [dojoCopyToClipboard]="posthogSessionReplayUrl">
            {{ posthogSessionReplayUrl }}
          </a>
        </p>
      }
      @if (state.showDebugLog) {
        <label>Debug log:</label>
        <div class="logs">
          <code class="whitespace-pre">
            {{ state.prettyDebugLog }}
          </code>
        </div>
      }

      <div class="mt-4 flex items-center gap-2">
        <dojo-button [dojoCopyToClipboard]="state.fullContextClipboardContent" class="grow">
          Copy the full context
        </dojo-button>
        <dojo-button [dojoCopyToClipboard]="state.lastLogEntryClipboardContent" fill="outline">
          Copy the last entry
        </dojo-button>
      </div>
    </div>
  `,
  styles: `
    .logs {
      @apply truncate rounded bg-neutral-50 p-2 !overflow-y-scroll;
      box-shadow: inset 0px -2px 8px 0px #a0a0a096;
      max-height: 100px;
    }
  `,
  host: {
    class: 'p-4',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssueReportDialogComponent {
  constructor(private readonly debuggingContextService: DebuggingContextService) {}

  readonly posthogSessionReplayUrl = posthog.get_session_replay_url();

  private readonly showDebugLog$ = this.debuggingContextService.contextHistory$.pipe(
    map((history) => history.some((entry) => Object.keys(entry.context).length > 0)),
    distinctUntilChanged(),
  );

  readonly prettyDebugLog$ = this.debuggingContextService.contextHistory$.pipe(
    map((history) =>
      history
        .map((entry) => {
          if (!Object.keys(entry.context).length) {
            return undefined;
          }
          return `‣ ${entry.timestamp.toISOString()} ${JSON.stringify(entry.context)}`;
        })
        .filter(isNotNullish)
        .map((content) => content.trim())
        .join('\n'),
    ),
  );

  readonly state = connectState({
    showDebugLog: this.showDebugLog$,
    prettyDebugLog: this.prettyDebugLog$,
    fullContextClipboardContent: combineLatest([this.debuggingContextService.contextHistory$, this.showDebugLog$]).pipe(
      map(([log, showDebugLog]) => {
        const logSection: string = showDebugLog ? `\n\nDebug log:\n${JSON.stringify(log)}` : '';
        return `PostHog session: ${this.posthogSessionReplayUrl}${logSection}`;
      }),
    ),
    lastLogEntryClipboardContent: combineLatest([
      this.debuggingContextService.contextHistory$,
      this.showDebugLog$,
    ]).pipe(
      map(([history, showDebugLog]) => {
        if (!showDebugLog) {
          return '';
        }
        return JSON.stringify(history.at(-1));
      }),
    ),
  });
}
